import axios from 'axios';
import { authXConfig } from 'router/authx-config';
import { Logger } from 'common/logger';
import { isLocalEnv } from 'common/config';

export const setupInstanceInterceptors = (axiosInstance, dispatch) => {
    axiosInstance.interceptors.response.use(
        function(response) {
            return response;
        },
        function(error) {
            if (!error.response || error.response.status !== 404) {
                dispatch({ type: 'appData/setModelError', modelError: error });
            }
            return Promise.reject(error);
        }
    );
};

const axiosInst = axios.create({
    baseURL: authXConfig.IAM.baseURL,
});

axiosInst.interceptors.request.use((config) => {
    const code = localStorage.getItem('auth_code');

    if (code) {
        config.headers = {
            ...config.headers,
            code,
        };
    }

    return config;
});

export const configureIamAPI = (token) => {
    Logger.debug('Configuring configureIamAPI, setting token.');
    let bearerToken = `Bearer ${token}`;
    if (isLocalEnv()) {
        bearerToken = '';
    }

    axiosInst.defaults.headers.common.Authorization = bearerToken;
};

export const fetchUserById = (userId) => {
    return axiosInst.get(`/api/v2/users/${userId}`);
};
export const batchCheckRights = (query) => {
    return axiosInst.post(`api/v1/security/batchcheck`, query);
};
export const checkPermission = ({ namespace, id, permission }) => {
    return axiosInst.get(`/api/v1/objects/${namespace}/${id}/${permission}/check`);
};
export const checkUserGroups = ({ userId }) => {
    return axiosInst.get(`/api/v2/users/${userId}/groups`);
};
export const checkRights = (query) => {
    return axiosInst.post(`api/v1/security/check`, query);
};

export const checkAllPrivileges = async (token, { parkObjects, ccrObjectId, ccrPrivilegeId }) => {
    const query = [];

    parkObjects.forEach(({ objectId, privilegeIdOperations, privilegeIdReports }) => {
        query.push({ subject: { token }, privilegeId: privilegeIdOperations, objectId });
        query.push({ subject: { token }, privilegeId: privilegeIdReports, objectId });
    });

    query.push({ subject: { token }, privilegeId: ccrPrivilegeId, objectId: ccrObjectId });

    const { data } = await batchCheckRights(query);

    const hasResult = ([, res]) => res.result;
    const toPermission = ([perm]) => perm;

    return data.filter(hasResult).map(toPermission);
};

export const checkPermissionsForDashboard = async () => {
    try {
        const permissionsToCheck = [{ namespace: 'root', id: 'sys:root:pb-ccr', permission: 'view' }];

        // Map over permissions and handle fulfilled/rejected states
        const results = await Promise.allSettled(
            permissionsToCheck.map((permission) =>
                checkPermission(permission)
                    .then((result) => ({
                        ...result.data,
                        privilegeId: permission.permission,
                        objectId: permission.id,
                        status: 'fulfilled',
                    }))
                    .catch((error) => ({
                        status: 'rejected',
                        error,
                        privilegeId: permission.permission,
                        objectId: permission.id,
                    }))
            )
        );

        // Filter and map to return only the allowed true value results
        return results
            .filter(({ status, value }) => status === 'fulfilled' && value.allowed)
            .map(({ value }) => ({
                privilegeId: value.privilegeId,
                objectId: value.objectId,
                ...value,
            }));
    } catch (error) {
        console.error('Error checking permissions:', error);
        throw error;
    }
};

export const fetchAccountRightsById = (accountId) => {
    return axiosInst.get(`/api/security/rights/subject/${accountId}`, {});
};

const getErrorHandler = (returnEmptyValue) => ({ response }) => {
    if (response.data.statusCode && response.data.statusCode === 'NoSuchAccessToken') {
        throw new Error('NoSuchAccessToken');
    }
    return returnEmptyValue;
};

const emptyValue = ({ id, name = '' }) => ({
    data: { name, id },
});

export const normalizeAuthUser = (authUser) => {
    Logger.info('Normalizing authenticated user from ', authUser);
    const prefix = 'urn:com.intertrust.platform.api';

    const user = {};
    user.userId = authUser.profile.sub;
    user.applicationId = authUser.profile[`${prefix}:application`];
    user.accountId = authUser.profile[`${prefix}:account`] || authUser.profile.sub;
    user.organizationId = authUser.profile[`${prefix}:organization`];
    user.organizationName = authUser.profile[`${prefix}:organization.name`] || [];
    user.permissions = authUser.profile[`${prefix}:permissions.v2`] || [];
    user.hasMultipleAccounts = !!authUser.profile[`${prefix}:organization.multiple`];

    return user;
};

export const fetchUserData = async (authData) => {
    const { accountId, userId } = authData;
    Logger.info(`Fetching user data from authX for accountId:${accountId} `);
    const accountRes = await fetchUserById(userId).catch(getErrorHandler(emptyValue({ id: accountId })));
    return {
        ...authData,
        account: accountRes.data,
        user: { ...accountRes.data, id: userId, accountId },
    };
};
